'use client'

import { useState, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Camera, X, ChevronLeft, Loader2 } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { compressImage, validateImageFile, revokeImagePreview } from '@/utils/imageCompression'
import { parseAuthToken } from '@/utils/base64'

interface FormData {
  title: string
  content: string
  images: File[]
  image_previews: string[]
}

export default function NewPostPage() {
  const router = useRouter()
  const { theme } = useTheme()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const imageInputRef = useRef<HTMLInputElement>(null)

  const isDark = theme === 'dark'

  const [formData, setFormData] = useState<FormData>({
    title: '',
    content: '',
    images: [],
    image_previews: []
  })

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    if (formData.images.length + files.length > 5) {
      setError('최대 5장까지 업로드 가능합니다.')
      return
    }

    setError('')
    const newImages: File[] = []
    const newPreviews: string[] = []

    for (const file of files) {
      const validation = validateImageFile(file)
      if (!validation.valid) {
        setError(validation.error || '이미지를 처리할 수 없습니다.')
        continue
      }

      try {
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
          maxSizeMB: 2
        })

        newImages.push(compressedFile)
        newPreviews.push(URL.createObjectURL(compressedFile))
      } catch (err) {
        console.error('이미지 압축 실패:', err)
        setError('이미지 처리 중 오류가 발생했습니다.')
      }
    }

    setFormData({
      ...formData,
      images: [...formData.images, ...newImages],
      image_previews: [...formData.image_previews, ...newPreviews]
    })
  }

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index)
    const newPreviews = formData.image_previews.filter((_, i) => i !== index)

    revokeImagePreview(formData.image_previews[index])

    setFormData({
      ...formData,
      images: newImages,
      image_previews: newPreviews
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('제목과 내용을 입력해주세요.')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        alert('로그인이 필요합니다')
        router.push('/login')
        return
      }

      const decoded = parseAuthToken(token)
      if (!decoded) {
        alert('로그인이 필요합니다.')
        router.push('/login')
        return
      }
      const supabase = getSupabaseClient()

      // 1. 게시글 생성
      const { data: post, error: postError } = await supabase
        .from('reviews')
        .insert({
          user_id: decoded.user_id,
          title: formData.title.trim(),
          content: formData.content.trim(),
          rating: 5,
          diving_spot: null,
          diving_pool: null,
          diving_date: null
        })
        .select()
        .single()

      if (postError) throw postError

      // 2. 이미지 업로드
      if (formData.images.length > 0) {
        for (let i = 0; i < formData.images.length; i++) {
          const image = formData.images[i]
          const fileExt = image.name.split('.').pop()
          const fileName = `${post.id}_${i + 1}_${Date.now()}.${fileExt}`

          const { error: uploadError } = await supabase.storage
            .from('review-images')
            .upload(fileName, image, { upsert: true })

          if (uploadError) {
            console.error('이미지 업로드 실패:', uploadError)
            continue
          }

          const { data: { publicUrl } } = supabase.storage
            .from('review-images')
            .getPublicUrl(fileName)

          await supabase
            .from('review_images')
            .insert({
              review_id: post.id,
              image_url: publicUrl,
              display_order: i + 1
            })
        }
      }

      router.push('/community/free-board')
    } catch (err) {
      console.error('게시글 작성 실패:', err)
      setError(err instanceof Error ? err.message : '게시글 작성 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`min-h-screen ${
      isDark ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* 헤더 */}
      <div className={`sub-sticky z-20 px-4 py-3 border-b ${
        isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <button
            onClick={() => router.back()}
            className={`flex items-center gap-1 text-sm ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
            뒤로
          </button>
          <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            글쓰기
          </h1>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.title.trim() || !formData.content.trim()}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
              isSubmitting || !formData.title.trim() || !formData.content.trim()
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : '등록'}
          </button>
        </div>
      </div>

      {/* 폼 */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-4">
        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600'
          }`}>
            {error}
          </div>
        )}

        {/* 제목 */}
        <div className="mb-4">
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="제목을 입력하세요"
            maxLength={200}
            className={`w-full px-4 py-3 text-base font-medium rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
          />
          <div className={`text-right text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {formData.title.length}/200
          </div>
        </div>

        {/* 내용 */}
        <div className="mb-4">
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="내용을 입력하세요"
            rows={10}
            maxLength={10000}
            className={`w-full px-4 py-3 text-sm rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
            }`}
            style={{ minHeight: '200px' }}
          />
          <div className={`text-right text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {formData.content.length}/10000
          </div>
        </div>

        {/* 이미지 업로드 */}
        <div className={`p-4 rounded-xl border ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            사진 첨부 (최대 5장)
          </label>

          <div className="grid grid-cols-5 gap-2 mb-3">
            {formData.image_previews.map((preview, index) => (
              <div key={index} className="relative aspect-square">
                <img
                  src={preview}
                  alt={`미리보기 ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {formData.images.length < 5 && (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className={`aspect-square rounded-lg border-2 border-dashed flex items-center justify-center transition-colors ${
                  isDark
                    ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-700'
                    : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                <Camera className={`w-6 h-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </button>
            )}
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            JPG, PNG, WebP 지원 (1920px로 자동 압축)
          </p>
        </div>

        <div className="h-20" />
      </form>
    </div>
  )
}
